import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { ICommunityResourceFile } from '@/types/residents/communityResource';
import { openExternalUrl } from '@/utils/common/linking';
import { showAlert } from '@/utils/common/alert';
import { RESOURCES_COLORS } from '@/constants/residents/mobile/resourcesColors';
import { MOBILE_TYPOGRAPHY, MOBILE_TEXT_STYLES } from '@/constants/residents/mobile/mobileTypography';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { DetailHeaderCard } from '@/components/residents/mobile/DetailHeaderCard';

// Resource components
import { ResourceIcon } from '@/components/residents/resources/ResourceIcon';

type ResourceDetailRouteProp = RouteProp<RootStackParamList, 'MobileResourceDetail'>;

/**
 * Mobile Resource Detail Screen
 * Full detail view with back navigation, header, info sections
 * Matches Figma design for Details screen
 */
export const MobileResourceDetailScreen: React.FC = () => {
  const route = useRoute<ResourceDetailRouteProp>();
  const navigation = useNavigation();
  const { resource } = route.params;

  const title = resource.name || resource.title || '';
  const description = resource.description || '';

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // Handle URL press
  const handleUrlPress = useCallback(async (url: string) => {
    const ok = await openExternalUrl(url);
    if (!ok) {
      showAlert('Error', 'Cannot open this URL');
    }
  }, []);

  // Handle file press
  const handleFilePress = useCallback(async (file: ICommunityResourceFile) => {
    const ok = await openExternalUrl(file.fileUrl);
    if (!ok) {
      showAlert('Error', `Cannot open ${file.label || `File ${file.id}`}`);
    }
  }, []);

  return (
    <BaseResidentMobileScreen
      screenName="Resources"
      darkBackground={true}
    >
      <MobileScreenLayout
        backgroundColor={RESOURCES_COLORS.background}
        contentGap={20}
        lightStatusBar={true}
      >
        {/* Back Navigation */}
        <BackLink
          label="Back to all resources"
          onPress={handleBack}
          color={RESOURCES_COLORS.linkBlue}
        />

        {/* Header Card */}
        <DetailHeaderCard
          title={title}
          description={description}
          icon={
            <ResourceIcon
              iconKey={resource.icon || resource.iconName}
              size={24}
              color={RESOURCES_COLORS.textPrimary}
            />
          }
          cardBackground={RESOURCES_COLORS.cardBackground}
          accentColor={RESOURCES_COLORS.linkPink}
          titleColor={RESOURCES_COLORS.textPrimary}
          descriptionColor={RESOURCES_COLORS.textPrimary}
        />

        {/* Information Section */}
        {resource.content && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>INFORMATION</Text>
            <Text style={styles.sectionText}>{resource.content}</Text>
          </View>
        )}

        {/* Website Section */}
        {resource.url && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>WEBSITE</Text>
            <TouchableOpacity onPress={() => handleUrlPress(resource.url!)}>
              <Text style={styles.linkText}>{resource.url}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Files Section */}
        {resource.files && resource.files.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>FILE</Text>
            {resource.files.map((file) => (
              <TouchableOpacity
                key={file.id}
                onPress={() => handleFilePress(file)}
                style={styles.fileItem}
              >
                <Text style={styles.linkText}>
                  {file.label || `File ${file.id}`}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </MobileScreenLayout>
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  section: {
    gap: 4,
  },
  sectionLabel: {
    ...MOBILE_TYPOGRAPHY.label,
    color: RESOURCES_COLORS.textMuted,
  },
  sectionText: {
    ...MOBILE_TYPOGRAPHY.bodyLarge,
    color: RESOURCES_COLORS.textPrimary,
  },
  linkText: {
    ...MOBILE_TYPOGRAPHY.bodyLarge,
    color: RESOURCES_COLORS.linkPink,
  },
  fileItem: {
    marginTop: 4,
  },
});
